import app from 'flarum/forum/app';

import Component, { ComponentAttrs } from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import avatar from 'flarum/common/helpers/avatar';
import icon from 'flarum/common/helpers/icon';
import SubtreeRetainer from 'flarum/common/utils/SubtreeRetainer';

import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';
import type User from 'flarum/common/models/User';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';

export interface IBackersAttrs extends ComponentAttrs {
  discussion: Discussion;
}

export default class Backers<CustomAttrs extends IBackersAttrs = IBackersAttrs> extends Component<CustomAttrs> {
  subtreeRetainer!: SubtreeRetainer;
  lastRenderBackers?: false | (User | undefined)[];
  loading: boolean = false;

  oninit(vnode: Mithril.Vnode) {
    super.oninit(vnode);

    if (!this.attrs.discussion.backers()) {
      this.load();
    }

    this.subtreeRetainer = new SubtreeRetainer(
      () => this.loading,
      () => this.attrs.discussion.backers()
    );
  }

  onbeforeupdate(vnode: Mithril.Vnode) {
    super.onbeforeupdate(vnode);

    return this.subtreeRetainer.needsRebuild();
  }

  onupdate(vnode: Mithril.Vnode) {
    // if (this.lastRenderBackers !== this.attrs.discussion.backers()) {
    //   setTimeout(() => m.redraw(), 0);
    //   this.lastRenderBackers = this.attrs.discussion.backers();
    //   this.load();
    // }
  }

  view() {
    return (
      <div className="Payments-backers">
        <div className="Payments-backers-container">{this.loading ? <LoadingIndicator /> : this.content()}</div>
      </div>
    );
  }

  content() {
    const backers = this.attrs.discussion.backers() || [];

    return (
      <>
        <div className="Payments-backers-header">{this.headerItems(backers).toArray()}</div>

        <div className="Payments-backers-body">
          {backers.length === 0 ? (
            <div className="Payments-backers-body-message">{app.translator.trans('floxum-payments.forum.backers-widget.none')}</div>
          ) : (
            this.backersView(backers)
          )}
        </div>
      </>
    );
  }

  headerItems(backers: (User | undefined)[]): ItemList<Mithril.Children> {
    const items = new ItemList<Mithril.Children>();

    items.add(
      'totalBounty',
      this.headerItemView(
        this.attrs.discussion.bountyCurrencySymbol(),
        this.attrs.discussion.totalBounty(),
        extractText(app.translator.trans('floxum-payments.forum.backers-widget.pledged'))
      )
    );

    items.add(
      'backerCount',
      this.headerItemView(
        icon('fas fa-hand-holding-heart'),
        backers.length,
        extractText(app.translator.trans('floxum-payments.forum.backers-widget.backers'))
      )
    );

    return items;
  }

  headerItemView(icon: Mithril.Children, value: number, label: string): Mithril.Children {
    return (
      <div className="Payments-backers-info">
        <div className="Payments-backers-info-value">
          <span className="Payments-backers-info-icon">{icon}</span>
          <span className="Payments-backers-info-number">{value}</span>
        </div>
        <div className="Payments-backers-info-label">{label}</div>
      </div>
    );
  }

  backersView(backers: (User | undefined)[]): Mithril.Children {
    const max = 15;

    return (
      <div className="Payments-backers-users">
        {backers.slice(0, max).map((user: any) => (
          <Link href={app.route('user', { username: user.slug() })} className="Payments-backers-users-item">
            <Tooltip text={user.displayName()}>{avatar(user)}</Tooltip>
          </Link>
        ))}
        {backers.length > max ? (
          <span className="Payments-backers-users-item Payments-backers-users-item--plus">
            <span className="Avatar">{`+${backers.length - max}`}</span>
          </span>
        ) : null}
      </div>
    );
  }

  async load() {
    this.loading = true;

    this.attrs.discussion = await app.store.find('discussions', this.attrs.discussion.id()!, {
      include: 'backers',
    });

    this.loading = false;

    m.redraw();
  }
}
